import { useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Draggable from 'react-draggable';
import { ArrowLeft, Save, AlertTriangle, Accessibility, Settings, Eye, EyeOff, RotateCcw, ZoomIn, ZoomOut, Plus, Trash2, RotateCw, Move } from 'lucide-react';
import { api } from '@/lib/api';
import type { CinemaScreen, CinemaSeat, CinemaSeatType, CinemaScreenSection, CinemaLayoutObject } from '@/types';

// Icon mapping for seat types
const SEAT_ICONS: Record<string, React.ReactNode> = {
  wheelchair: <Accessibility className="h-3 w-3" />,
  star: <span className="text-xs">★</span>,
  heart: <span className="text-xs">♥</span>,
  vip: <span className="text-[8px] font-bold">VIP</span>,
  premium: <span className="text-[8px] font-bold">P</span>,
};

// Layout object icons
const OBJECT_ICONS: Record<string, string> = {
  stage: 'STAGE',
  stairs: '↗↘',
  exit: 'EXIT',
  emergency_exit: '🚨 EXIT',
  aisle: '',
  barrier: '━━━',
  pillar: '◼',
};

// Preset canvas sizes
const CANVAS_PRESETS = [
  { label: 'Small (800x600)', width: 800, height: 600 },
  { label: 'Medium (1200x800)', width: 1200, height: 800 },
  { label: 'Large (1600x1000)', width: 1600, height: 1000 },
  { label: 'Extra Large (2000x1200)', width: 2000, height: 1200 },
  { label: 'Custom', width: 0, height: 0 },
];

export default function SeatLayoutDesigner() {
  const { venueId, screenId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLDivElement>(null);

  const [selectedSeats, setSelectedSeats] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<string>('');
  const [showRemovedSeats, setShowRemovedSeats] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedObject, setSelectedObject] = useState<string | null>(null);
  const [customWidth, setCustomWidth] = useState(800);
  const [customHeight, setCustomHeight] = useState(600);

  const { data: screen, refetch: refetchScreen } = useQuery<CinemaScreen>({
    queryKey: ['cinema-screen', screenId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens/${screenId}`);
      return response.data;
    },
  });

  const { data: sections, refetch: refetchSections } = useQuery<CinemaScreenSection[]>({
    queryKey: ['cinema-sections', screenId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens/${screenId}/sections`);
      return response.data;
    },
  });

  const { data: seats, isLoading, refetch: refetchSeats } = useQuery<CinemaSeat[]>({
    queryKey: ['cinema-screen-seats', screenId, showRemovedSeats],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens/${screenId}/seats`);
      return response.data;
    },
  });

  const { data: seatTypes } = useQuery<CinemaSeatType[]>({
    queryKey: ['cinema-seat-types', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/seat-types`);
      return response.data;
    },
  });

  const { data: layoutObjects, refetch: refetchObjects } = useQuery<CinemaLayoutObject[]>({
    queryKey: ['cinema-layout-objects', screenId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens/${screenId}/layout-objects`);
      return response.data;
    },
  });

  // Update section position (drag-drop)
  const updateSectionPositionMutation = useMutation({
    mutationFn: async ({ sectionId, positionX, positionY }: { sectionId: string; positionX: number; positionY: number }) => {
      await api.put(`/admin/venues/${venueId}/cinema/sections/${sectionId}/position`, { positionX, positionY });
    },
    onSuccess: () => {
      refetchSections();
    },
  });

  // Update section rotation
  const updateSectionRotationMutation = useMutation({
    mutationFn: async ({ sectionId, rotation }: { sectionId: string; rotation: number }) => {
      await api.put(`/admin/venues/${venueId}/cinema/sections/${sectionId}/rotation`, { rotation });
    },
    onSuccess: () => {
      refetchSections();
    },
  });

  // Bulk update seats
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ seatIds, updates }: { seatIds: string[]; updates: Record<string, unknown> }) => {
      await api.put(`/admin/venues/${venueId}/cinema/seats/bulk`, { seatIds, updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-screen-seats', screenId] });
      setSelectedSeats(new Set());
      setBulkAction('');
    },
  });

  // Toggle seat removal
  const toggleSeatRemovedMutation = useMutation({
    mutationFn: async (seatId: string) => {
      await api.put(`/admin/venues/${venueId}/cinema/seats/${seatId}/toggle-removed`);
    },
    onSuccess: () => {
      refetchSeats();
    },
  });

  // Screen position/size mutations
  const updateScreenPositionMutation = useMutation({
    mutationFn: async (data: { screenPositionX?: number; screenPositionY: number; screenWidthPx: number; screenRotation: number }) => {
      await api.put(`/admin/venues/${venueId}/cinema/screens/${screenId}/screen-position`, data);
    },
    onSuccess: () => {
      refetchScreen();
    },
  });

  const updateBackgroundColorMutation = useMutation({
    mutationFn: async (backgroundColor: string) => {
      await api.put(`/admin/venues/${venueId}/cinema/screens/${screenId}/background-color`, { backgroundColor });
    },
    onSuccess: () => {
      refetchScreen();
    },
  });

  const updateLayoutSizeMutation = useMutation({
    mutationFn: async ({ layoutWidth, layoutHeight }: { layoutWidth: number; layoutHeight: number }) => {
      await api.put(`/admin/venues/${venueId}/cinema/screens/${screenId}/layout-size`, { layoutWidth, layoutHeight });
    },
    onSuccess: () => {
      refetchScreen();
    },
  });

  // Layout object mutations
  const createLayoutObjectMutation = useMutation({
    mutationFn: async (data: { objectType: string; name?: string; positionX: number; positionY: number; widthPx: number; heightPx: number }) => {
      await api.post(`/admin/venues/${venueId}/cinema/screens/${screenId}/layout-objects`, data);
    },
    onSuccess: () => {
      refetchObjects();
    },
  });

  const updateLayoutObjectPositionMutation = useMutation({
    mutationFn: async ({ objectId, positionX, positionY }: { objectId: string; positionX: number; positionY: number }) => {
      await api.put(`/admin/venues/${venueId}/cinema/layout-objects/${objectId}/position`, { positionX, positionY });
    },
    onSuccess: () => {
      refetchObjects();
    },
  });

  const deleteLayoutObjectMutation = useMutation({
    mutationFn: async (objectId: string) => {
      await api.delete(`/admin/venues/${venueId}/cinema/layout-objects/${objectId}`);
    },
    onSuccess: () => {
      refetchObjects();
      setSelectedObject(null);
    },
  });

  // Group seats by section
  const seatsBySection = useMemo(() => {
    if (!seats) return new Map<string, CinemaSeat[]>();
    const grouped = new Map<string, CinemaSeat[]>();
    seats.forEach((seat) => {
      const sectionSeats = grouped.get(seat.section_id) || [];
      sectionSeats.push(seat);
      grouped.set(seat.section_id, sectionSeats);
    });
    return grouped;
  }, [seats]);

  const toggleSeat = useCallback((seatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDragging) return;

    const newSelected = new Set(selectedSeats);
    if (newSelected.has(seatId)) {
      newSelected.delete(seatId);
    } else {
      newSelected.add(seatId);
    }
    setSelectedSeats(newSelected);
  }, [selectedSeats, isDragging]);

  const selectAllInSection = (sectionId: string) => {
    const sectionSeats = seatsBySection.get(sectionId) || [];
    const newSelected = new Set(selectedSeats);
    sectionSeats.forEach((s) => newSelected.add(s.id));
    setSelectedSeats(newSelected);
  };

  const clearSelection = () => {
    setSelectedSeats(new Set());
  };

  const applyBulkAction = () => {
    if (!bulkAction || selectedSeats.size === 0) return;

    const seatIds = Array.from(selectedSeats);
    let updates: Record<string, unknown> = {};

    if (bulkAction === 'damaged') {
      updates = { isDamaged: true };
    } else if (bulkAction === 'undamaged') {
      updates = { isDamaged: false };
    } else if (bulkAction === 'accessible') {
      updates = { isAccessible: true };
    } else if (bulkAction === 'not-accessible') {
      updates = { isAccessible: false };
    } else if (bulkAction.startsWith('type-')) {
      updates = { seatTypeId: bulkAction.replace('type-', '') };
    } else if (bulkAction === 'type-none') {
      updates = { seatTypeId: null };
    }

    bulkUpdateMutation.mutate({ seatIds, updates });
  };

  const handleSectionDragStop = (sectionId: string, x: number, y: number) => {
    setIsDragging(false);
    updateSectionPositionMutation.mutate({ sectionId, positionX: x, positionY: y });
  };

  const handleSectionRotate = (sectionId: string, currentRotation: number, delta: number) => {
    const newRotation = (currentRotation + delta + 360) % 360;
    updateSectionRotationMutation.mutate({ sectionId, rotation: newRotation });
  };

  const handleScreenDragStop = (x: number, y: number) => {
    setIsDragging(false);
    updateScreenPositionMutation.mutate({
      screenPositionX: x,
      screenPositionY: y,
      screenWidthPx: screen?.screen_width_px || 400,
      screenRotation: screen?.screen_rotation || 0,
    });
  };

  const handleAddObject = (objectType: string) => {
    createLayoutObjectMutation.mutate({
      objectType,
      name: objectType.charAt(0).toUpperCase() + objectType.slice(1).replace('_', ' '),
      positionX: 100,
      positionY: 100,
      widthPx: objectType === 'pillar' ? 40 : objectType === 'aisle' ? 20 : 100,
      heightPx: objectType === 'pillar' ? 40 : objectType === 'aisle' ? 200 : 40,
    });
  };

  const handleObjectDragStop = (objectId: string, x: number, y: number) => {
    setIsDragging(false);
    updateLayoutObjectPositionMutation.mutate({ objectId, positionX: x, positionY: y });
  };

  const handleCanvasSizeChange = (preset: typeof CANVAS_PRESETS[number]) => {
    if (preset.width === 0) {
      // Custom - use current values
      updateLayoutSizeMutation.mutate({ layoutWidth: customWidth, layoutHeight: customHeight });
    } else {
      updateLayoutSizeMutation.mutate({ layoutWidth: preset.width, layoutHeight: preset.height });
    }
  };

  const getSeatColor = (seat: CinemaSeat) => {
    if (seat.is_damaged) return '#EF4444'; // Red for damaged
    if (seat.is_removed) return '#D1D5DB'; // Light gray for removed
    if (seat.seat_type_color) return seat.seat_type_color;
    return '#9CA3AF'; // Gray for no type
  };

  const getSeatWidth = (seat: CinemaSeat) => {
    const baseWidth = seat.width_px || 30;
    const multiplier = seat.seat_type_width_multiplier || 1;
    return baseWidth * multiplier;
  };

  // Layout dimensions from screen settings
  const layoutWidth = screen?.layout_width || 800;
  const layoutHeight = screen?.layout_height || 600;
  const screenWidthPx = screen?.screen_width_px || 400;
  const screenPositionX = screen?.screen_position_x;
  const screenPositionY = screen?.screen_position_y || 20;
  const screenRotation = screen?.screen_rotation || 0;
  const backgroundColor = screen?.background_color || '#f3f4f6';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4 flex-shrink-0">
        <button
          onClick={() => navigate(`/venues/${venueId}/cinema/screens/${screenId}/sections`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Seat Layout Designer</h1>
          <p className="text-sm text-gray-500">{screen?.name} - Drag sections to position, click seats to select</p>
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`btn ${showSettings ? 'btn-primary' : 'btn-secondary'}`}
        >
          <Settings className="h-4 w-4 mr-2" />
          Layout Settings
        </button>
        <button
          onClick={() => navigate(`/venues/${venueId}/cinema/screens/${screenId}/sections`)}
          className="btn btn-secondary"
        >
          Edit Sections
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="card mb-4 p-4 flex-shrink-0">
          <h3 className="font-medium text-gray-900 mb-4">Layout Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Canvas Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Canvas Size</label>
              <div className="space-y-2">
                {CANVAS_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => {
                      if (preset.width === 0) {
                        setCustomWidth(layoutWidth);
                        setCustomHeight(layoutHeight);
                      }
                      handleCanvasSizeChange(preset);
                    }}
                    className={`block w-full text-left px-3 py-2 text-sm rounded ${
                      (preset.width === layoutWidth && preset.height === layoutHeight) ||
                      (preset.width === 0 && !CANVAS_PRESETS.some(p => p.width === layoutWidth && p.height === layoutHeight && p.width !== 0))
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
                <div className="flex gap-2 mt-2">
                  <input
                    type="number"
                    value={customWidth}
                    onChange={(e) => setCustomWidth(parseInt(e.target.value) || 800)}
                    className="input w-24"
                    placeholder="Width"
                  />
                  <span className="self-center">x</span>
                  <input
                    type="number"
                    value={customHeight}
                    onChange={(e) => setCustomHeight(parseInt(e.target.value) || 600)}
                    className="input w-24"
                    placeholder="Height"
                  />
                  <button
                    onClick={() => updateLayoutSizeMutation.mutate({ layoutWidth: customWidth, layoutHeight: customHeight })}
                    className="btn btn-primary btn-sm"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>

            {/* Background Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={backgroundColor}
                  onChange={(e) => updateBackgroundColorMutation.mutate(e.target.value)}
                  className="h-10 w-16 cursor-pointer rounded border"
                />
                <input
                  type="text"
                  value={backgroundColor}
                  onChange={(e) => updateBackgroundColorMutation.mutate(e.target.value)}
                  className="input w-28"
                />
                <div className="flex gap-1">
                  {['#f3f4f6', '#1f2937', '#dbeafe', '#fef3c7', '#d1fae5'].map((color) => (
                    <button
                      key={color}
                      onClick={() => updateBackgroundColorMutation.mutate(color)}
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Add Objects */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Add Layout Objects</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { type: 'stage', label: 'Stage' },
                  { type: 'stairs', label: 'Stairs' },
                  { type: 'exit', label: 'Exit' },
                  { type: 'emergency_exit', label: 'Emergency Exit' },
                  { type: 'aisle', label: 'Aisle' },
                  { type: 'barrier', label: 'Barrier' },
                  { type: 'pillar', label: 'Pillar' },
                ].map(({ type, label }) => (
                  <button
                    key={type}
                    onClick={() => handleAddObject(type)}
                    className="btn btn-sm bg-gray-100 hover:bg-gray-200 text-gray-700"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls Bar */}
      <div className="card mb-4 p-4 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-6">
          {/* Legend */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Legend:</span>
            {seatTypes?.map((type) => (
              <div key={type.id} className="flex items-center gap-2">
                <div
                  className="h-4 w-4 rounded flex items-center justify-center text-white"
                  style={{ backgroundColor: type.color }}
                >
                  {type.icon && SEAT_ICONS[type.icon]}
                </div>
                <span className="text-sm">{type.name}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-red-500" />
              <span className="text-sm">Damaged</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-gray-400" />
              <span className="text-sm">No Type</span>
            </div>
          </div>

          <div className="flex-1" />

          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoom(z => Math.max(0.25, z - 0.1))}
              className="p-1.5 rounded hover:bg-gray-100"
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-600 w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => setZoom(z => Math.min(3, z + 0.1))}
              className="p-1.5 rounded hover:bg-gray-100"
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <button
              onClick={() => setZoom(1)}
              className="p-1.5 rounded hover:bg-gray-100"
              title="Reset zoom"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>

          {/* Show removed toggle */}
          <button
            onClick={() => setShowRemovedSeats(!showRemovedSeats)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
              showRemovedSeats ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {showRemovedSeats ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {showRemovedSeats ? 'Showing removed' : 'Hidden removed'}
          </button>

          {/* Selection info */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {selectedSeats.size} seat{selectedSeats.size !== 1 ? 's' : ''} selected
            </span>
            {selectedSeats.size > 0 && (
              <button
                onClick={clearSelection}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Bulk actions */}
        {selectedSeats.size > 0 && (
          <div className="flex items-center gap-4 mt-4 pt-4 border-t">
            <span className="text-sm font-medium text-gray-700">Bulk Action:</span>
            <select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              className="input w-48"
            >
              <option value="">Select action...</option>
              <optgroup label="Seat Type">
                {seatTypes?.map((type) => (
                  <option key={type.id} value={`type-${type.id}`}>
                    Set to {type.name}
                  </option>
                ))}
                <option value="type-none">Remove type</option>
              </optgroup>
              <optgroup label="Damage Status">
                <option value="damaged">Mark as Damaged</option>
                <option value="undamaged">Mark as Undamaged</option>
              </optgroup>
              <optgroup label="Accessibility">
                <option value="accessible">Mark as Accessible</option>
                <option value="not-accessible">Mark as Not Accessible</option>
              </optgroup>
            </select>
            <button
              onClick={applyBulkAction}
              disabled={!bulkAction || bulkUpdateMutation.isPending}
              className="btn btn-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              {bulkUpdateMutation.isPending ? 'Applying...' : 'Apply'}
            </button>
          </div>
        )}
      </div>

      {/* Layout Canvas */}
      <div className="flex-1 overflow-auto bg-gray-200 rounded-lg p-4">
        <div
          ref={canvasRef}
          className="relative mx-auto shadow-lg rounded"
          style={{
            width: layoutWidth * zoom,
            height: layoutHeight * zoom,
            backgroundColor,
            minWidth: '100%',
          }}
        >
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              width: layoutWidth,
              height: layoutHeight,
              position: 'relative',
            }}
          >
            {/* Screen/Stage indicator - draggable */}
            <Draggable
              position={{ x: screenPositionX ?? (layoutWidth / 2 - screenWidthPx / 2), y: screenPositionY }}
              onStart={() => setIsDragging(true)}
              onStop={(_, data) => handleScreenDragStop(data.x, data.y)}
              bounds="parent"
              handle=".screen-handle"
            >
              <div
                className="absolute bg-gray-800 text-white text-center rounded-t-lg flex flex-col cursor-move screen-handle"
                style={{
                  width: screenWidthPx,
                  height: 40,
                  transform: screenRotation ? `rotate(${screenRotation}deg)` : undefined,
                  transformOrigin: 'center center',
                }}
              >
                <div className="flex-1 flex items-center justify-center">
                  <span className="text-sm font-medium">SCREEN</span>
                </div>
                <div className="flex justify-center gap-1 pb-1 opacity-50 hover:opacity-100">
                  <Move className="h-3 w-3" />
                </div>
              </div>
            </Draggable>

            {/* Layout Objects */}
            {layoutObjects?.map((obj) => (
              <Draggable
                key={obj.id}
                position={{ x: obj.position_x, y: obj.position_y }}
                onStart={() => setIsDragging(true)}
                onStop={(_, data) => handleObjectDragStop(obj.id, data.x, data.y)}
                bounds="parent"
              >
                <div
                  className={`absolute flex items-center justify-center cursor-move border-2 ${
                    selectedObject === obj.id ? 'border-indigo-500' : 'border-transparent'
                  }`}
                  style={{
                    width: obj.width_px,
                    height: obj.height_px,
                    backgroundColor: obj.color || '#6B7280',
                    transform: obj.rotation ? `rotate(${obj.rotation}deg)` : undefined,
                    transformOrigin: 'center center',
                    borderRadius: obj.object_type === 'pillar' ? '50%' : '4px',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedObject(obj.id);
                  }}
                >
                  <span className="text-white text-xs font-medium text-center px-1">
                    {obj.name || OBJECT_ICONS[obj.object_type] || obj.object_type}
                  </span>
                  {selectedObject === obj.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteLayoutObjectMutation.mutate(obj.id);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </Draggable>
            ))}

            {/* Sections with drag-and-drop */}
            {sections?.map((section) => {
              const sectionSeats = seatsBySection.get(section.id) || [];

              // Calculate section bounds from seats
              let minX = Infinity, maxX = 0, minY = Infinity, maxY = 0;
              sectionSeats.forEach(seat => {
                const seatWidth = getSeatWidth(seat);
                const seatHeight = seat.height_px || 30;
                minX = Math.min(minX, seat.position_x);
                maxX = Math.max(maxX, seat.position_x + seatWidth);
                minY = Math.min(minY, seat.position_y);
                maxY = Math.max(maxY, seat.position_y + seatHeight);
              });

              const sectionWidth = section.width_px || (maxX - minX + 40) || 300;
              const sectionHeight = section.height_px || (maxY - minY + 60) || 200;

              return (
                <Draggable
                  key={section.id}
                  defaultPosition={{ x: section.position_x, y: section.position_y }}
                  onStart={() => setIsDragging(true)}
                  onStop={(_, data) => handleSectionDragStop(section.id, data.x, data.y)}
                  bounds="parent"
                  handle=".section-handle"
                >
                  <div
                    className="absolute bg-white/90 border border-gray-300 rounded-lg shadow-sm"
                    style={{
                      width: sectionWidth,
                      minHeight: sectionHeight,
                      transform: section.rotation ? `rotate(${section.rotation}deg)` : undefined,
                      transformOrigin: 'center center',
                    }}
                  >
                    {/* Section header (drag handle) */}
                    <div className="section-handle cursor-move bg-gray-200 rounded-t-lg px-3 py-2 flex items-center justify-between border-b">
                      <span className="font-medium text-sm text-gray-700">{section.name}</span>
                      <div className="flex items-center gap-2">
                        {/* Rotation controls */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSectionRotate(section.id, section.rotation || 0, -15);
                          }}
                          className="p-1 hover:bg-gray-300 rounded"
                          title="Rotate left 15°"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </button>
                        <span className="text-xs text-gray-500">{section.rotation || 0}°</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSectionRotate(section.id, section.rotation || 0, 15);
                          }}
                          className="p-1 hover:bg-gray-300 rounded"
                          title="Rotate right 15°"
                        >
                          <RotateCw className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            selectAllInSection(section.id);
                          }}
                          className="text-xs text-indigo-600 hover:text-indigo-800 ml-2"
                        >
                          Select All
                        </button>
                      </div>
                    </div>

                    {/* Seats container */}
                    <div className="relative p-4" style={{ minHeight: sectionHeight - 40 }}>
                      {sectionSeats.map((seat) => {
                        const seatWidth = getSeatWidth(seat);
                        const seatHeight = seat.height_px || 30;

                        // Skip removed seats if not showing them
                        if (seat.is_removed && !showRemovedSeats) return null;

                        return (
                          <button
                            key={seat.id}
                            onClick={(e) => toggleSeat(seat.id, e)}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              toggleSeatRemovedMutation.mutate(seat.id);
                            }}
                            className={`
                              absolute rounded text-xs font-medium transition-all flex items-center justify-center
                              ${selectedSeats.has(seat.id) ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}
                              ${seat.is_damaged ? 'opacity-50' : ''}
                              ${seat.is_removed ? 'opacity-30 border-2 border-dashed border-gray-400' : ''}
                            `}
                            style={{
                              left: seat.position_x - minX,
                              top: seat.position_y - minY,
                              width: seatWidth,
                              height: seatHeight,
                              backgroundColor: getSeatColor(seat),
                              color: '#fff',
                            }}
                            title={`${seat.row_label}${seat.seat_number}${seat.seat_type_name ? ` (${seat.seat_type_name})` : ''}${seat.is_damaged ? ' - DAMAGED' : ''}${seat.is_accessible ? ' - Accessible' : ''}${seat.is_removed ? ' - REMOVED (double-click to restore)' : ''}\nDouble-click to toggle removal`}
                          >
                            <span className="relative">
                              {seat.seat_number}
                              {seat.seat_type_icon && SEAT_ICONS[seat.seat_type_icon] && (
                                <span className="absolute -top-1 -right-1">
                                  {SEAT_ICONS[seat.seat_type_icon]}
                                </span>
                              )}
                              {seat.is_accessible && (
                                <Accessibility className="h-2 w-2 absolute -top-1 -right-1" />
                              )}
                              {seat.is_damaged && (
                                <AlertTriangle className="h-2 w-2 absolute -bottom-1 -right-1" />
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </Draggable>
              );
            })}

            {(!sections || sections.length === 0) && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <p className="mb-4">No sections configured for this screen.</p>
                  <button
                    onClick={() => navigate(`/venues/${venueId}/cinema/screens/${screenId}/sections`)}
                    className="btn btn-primary"
                  >
                    Configure Sections
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help text */}
      <div className="mt-4 text-xs text-gray-500 flex-shrink-0">
        <p>
          <strong>Tips:</strong> Drag section headers to reposition. Use rotation buttons to angle sections.
          Click seats to select, then use bulk actions. Double-click a seat to toggle removal.
          Drag the screen to reposition it. Use zoom controls for detailed view.
        </p>
      </div>
    </div>
  );
}
